import SystemSettings from './systemSettings';
import Pendo from './pendo';
import Features from '../features';
import PrivilegeManager, { Privilege } from './privilegeManager';

class DataCache extends SystemSettings {
  private currentUser: any = null;

  private accountPrivileges = new PrivilegeManager();

  private setUserSystemSettings(settings: Map<string, string>): void {
    this.setSystemSettings(settings);
  }

  private initializePendo() {
    const apiKey = process.env[SystemSettings.PENDO_API_KEY] as string;
    const defaultApiKey = process.env[SystemSettings.PENDO_DEFAULT_API_KEY] as string;
    const pendoApiKey = this.getSystemSetting(apiKey, defaultApiKey);
    Pendo.init(pendoApiKey);
    // Here we are removing the GWT MastHead Help Icon
    Pendo.removeGwtMastHeadHelpIcon();
    Pendo.addDismissResourceCenterHandler();
  }

  private setCurrentUserFromUserResult(userResult: any) {
    if (userResult && userResult.user) {
      this.currentUser = userResult.user;
    }
  }

  private setUserAccountPrivileges() {
    if (this.currentUser) {
      const privileges = this.currentUser.Privilege ?? [];
      this.accountPrivileges.setAccountPrivileges(privileges);
    }
  }

  private changeAccount(accountSwitch: boolean) {
    if (this.currentUser) {
      const { email, Account } = this.currentUser;
      const { accountId, status } = Account;
      if (accountSwitch) {
        Pendo.updateOptions(accountId, status);
      } else {
        Pendo.initialize(email, accountId, status);
      }
    }
  }

  private getSystemSettingAsBooleanValue(settingName: string, defaultValue: any): boolean {
    return this.getSystemSettingAsBoolean(settingName, defaultValue);
  }

  private hasReadWriteBuildAccess(): boolean {
    return this.accountPrivileges.hasAccountPrivilege(Privilege.BUILD);
  }

  private hasAccountPrivilege(privilege: Privilege): boolean {
    return this.accountPrivileges.hasAccountPrivilege(privilege);
  }

  public hasAdminPrivilege(): boolean {
    return this.accountPrivileges.isAccountAdmin(Privilege.ACCOUNT_ADMIN);
  }

  public getAccountPrivileges(): any {
    return this.accountPrivileges.getAccountPrivileges();
  }

  public setCurrentUser(userResult: any) {
    let systemSettings = new Map();
    if (userResult && userResult.systemSettings
      && Object.keys(userResult.systemSettings).length > 0) {
      systemSettings = new Map(Object.entries(userResult.systemSettings));
    }
    // Storing the System Settings here
    this.setUserSystemSettings(systemSettings);

    // Storing the Current user here
    this.setCurrentUserFromUserResult(userResult);

    // Storing the User Account Priviliges here after setting the current user
    this.setUserAccountPrivileges();

    // Pendo Initialization will be done after saving the user and system settings.
    this.initializePendo();
    this.changeAccount(false);
  }

  public isEventStreamVisible(): boolean {
    if (this.currentUser) {
      const isESEnabled = Features.isEventStreamsFeatureEnabled(this.currentUser);
      const isESPlatformGaFlagEnabled = this.getSystemSettingAsBooleanValue(
        SystemSettings.ENABLE_ES_PLATFORM_GA,
        false,
      );
      const esExtendedProperty = Features.getEventStreamsExtendedProperty(this.currentUser);
      const esExtendedPropertyAsInt = parseInt(esExtendedProperty, 10);
      if ((!isESEnabled && (esExtendedPropertyAsInt > -1))
      || (!isESEnabled && !isESPlatformGaFlagEnabled)) {
        return false;
      }
      return true;
    }
    return false;
  }

  public isEventStreamReactUIEnabled(): boolean {
    return this.getSystemSettingAsBooleanValue(SystemSettings.ENABLE_ES_REACT_UI, true);
  }

  public getDiscoverURL(defaultValue:string): string {
    return this.getSystemSetting(SystemSettings.DISCOVER_URL, defaultValue);
  }

  public isPublisherUIEnabled(): boolean {
    return this.getSystemSettingAsBooleanValue(SystemSettings.ENABLE_PUBLISHER_UI, false);
  }

  public canUseBundles(): boolean {
    if (this.currentUser) {
      return Features.isBundleUIFeatureEnabled(this.currentUser) && this.hasReadWriteBuildAccess();
    }
    return false;
  }

  public canUseCatalogsOrSpaces(): boolean {
    if (this.currentUser) {
      return (this.getSystemSettingAsBooleanValue(
        SystemSettings.ENABLE_CATALOG_SERVICE_ADMIN_UI,
        false,
      )
        && this.hasAccountPrivilege(Privilege.ACCT_GRP_MGMT)
        && Features.isAcctGrpFeatureEnabled(this.currentUser)
        && Features.isCustomCatalogsFeatureEnabled(this.currentUser));
    }
    return false;
  }

  public canUseIPacks(): boolean {
    if (this.currentUser) {
      return this.hasAccountPrivilege(Privilege.INTEGRATION_PACK)
              && Features.isIntegrationPacksFeatureEnabled(this.currentUser);
    }
    return false;
  }

  public canUseProcessLibrary(): boolean {
    if (this.currentUser) {
      return this.hasAccountPrivilege(Privilege.PROCESS_LIBRARY)
            && Features.isProcessLibraryFeatureEnabled(this.currentUser);
    }
    return false;
  }
}

export default DataCache;
